import { useState, type FormEvent } from "react";
import { Select, NumberField, Button, Field } from "../components/rumonai";
import { LightMotion } from "../components/LightMotion";
import { platformPost, platformRequest, type MailView } from "./api";
import {
  CommandFeedback, PageHeading, PlatformError, PlatformLoading, usePlatformCommand, usePlatformRead
} from "./shared";

/**
 * 邮件服务: the mail service the Hub sends account mail through -- the links
 * that confirm an address and reset a forgotten password.
 *
 * Any mail service that accepts SMTP will do; the page names none. The password
 * is write-only: it is sent when typed, kept by the Hub, and never drawn again,
 * so an empty box means "keep the one held". A test mail goes with the settings
 * as saved, so the page asks for them to be saved first rather than testing
 * something else than what accounts will use.
 */
export function MailSettings() {
  const reading = usePlatformRead<MailView>("/api/admin/mail");
  // Held here, not in the form: a saved form is drawn again from what the Hub now holds, and the
  // answer to the save, and the address a test goes to, have to outlive that.
  const saving = usePlatformCommand();
  const testing = usePlatformCommand();
  const [to, setTo] = useState("");
  return <>
    <PageHeading title="邮件服务"><p>用户确认邮箱、忘记密码时，链接通过这里发出。</p></PageHeading>
    {reading.error ? <PlatformError error={reading.error} retry={reading.reload} /> : null}
    {!reading.data && reading.loading ? <PlatformLoading label="正在读取邮件设置" /> : null}
    {reading.data ? <MailForm key={JSON.stringify(reading.data.mail)} view={reading.data} reload={reading.reload}
      saving={saving} testing={testing} to={to} setTo={setTo} /> : null}
  </>;
}

type Command = ReturnType<typeof usePlatformCommand>;

function MailForm({ view, reload, saving, testing, to, setTo }: {
  view: MailView; reload: () => void; saving: Command; testing: Command; to: string; setTo: (value: string) => void;
}) {
  const [host, setHost] = useState(view.mail.host);
  const [tls, setTls] = useState(view.mail.tls);
  const [port, setPort] = useState(String(view.mail.port));
  const [user, setUser] = useState(view.mail.user);
  const [password, setPassword] = useState("");
  const [sender, setSender] = useState(view.mail.sender);
  const dirty = host.trim() !== view.mail.host || tls !== view.mail.tls || Number(port) !== view.mail.port
    || user.trim() !== view.mail.user || sender.trim() !== view.mail.sender || password !== "";
  const complete = Boolean(host.trim() && sender.trim() && /^\d+$/.test(port) && (!user.trim() || password || view.mail.password_present));
  const chooseTls = (next: string) => {
    // A port still at the old way's usual number follows the change; one the admin chose stays.
    const usual = (mode: string) => String(view.tls_modes.find(row => row.value === mode)?.port ?? "");
    if (port === usual(tls)) setPort(usual(next));
    setTls(next);
  };
  const save = (event: FormEvent) => {
    event.preventDefault();
    if (!dirty || !complete) return;
    void saving.run(async () => {
      await platformRequest<MailView>("/api/admin/mail", {
        method: "PUT",
        body: JSON.stringify({
          mail: { host: host.trim(), port: Number(port), tls, user: user.trim(), sender: sender.trim() },
          ...(password ? { password } : {}),
          reason: "设置邮件服务",
        }),
      });
      setPassword("");
      saving.setNotice("邮件设置已保存。");
      reload();
    });
  };
  const test = (event: FormEvent) => {
    event.preventDefault();
    if (dirty || !to.trim()) return;
    void testing.run(async () => {
      await platformPost("/api/admin/mail/test", { to: to.trim() });
      testing.setNotice(`测试邮件已发到 ${to.trim()}，请到收件箱确认。`);
    });
  };
  return <>
    <form className="platform-settings admin-mail" noValidate onSubmit={save} aria-busy={saving.pending}>
      <fieldset disabled={saving.pending}>
        <legend>发信服务</legend>
        <p>
          {view.ready ? "邮件服务可以使用。"
            : !view.configured ? "还没有设置邮件服务，用户暂时不能用邮箱找回密码。"
            : "设置还不完整，邮件暂时发不出去。"}
          {view.link_base ? ` 邮件里的链接会指向 ${view.link_base}。`
            : " Hub 还没有设置公开地址，邮件里的链接没有地方可去；请在启动 Hub 时设置公开地址。"}
        </p>
        <div className="platform-setting-grid">
          <Field className="platform-setting" label="服务器地址">
            <input id="mail-host" className="text-input" name="host" autoComplete="off" spellCheck={false}
              placeholder="smtp.example.com" value={host} onChange={event => setHost(event.target.value)} />
          </Field>
          <Field className="platform-setting" label="连接加密">
            <Select id="mail-tls" label="连接加密" name="tls" value={tls} onValueChange={chooseTls}
              disabled={saving.pending} items={view.tls_modes.map(row => ({ value: row.value, label: row.title }))} />
          </Field>
          <Field className="platform-setting" label="端口">
            <NumberField id="mail-port" label="端口" name="port" min={1} max={65535} inputMode="numeric"
              value={port} disabled={saving.pending} onChange={event => setPort(event.target.value)} />
          </Field>
          <Field className="platform-setting" label="用户名" hint="不需要登录的内部中继可以留空。">
            <input id="mail-user" className="text-input" name="user" autoComplete="off" spellCheck={false}
              value={user} onChange={event => setUser(event.target.value)} />
          </Field>
          <Field className="platform-setting" label={`密码${view.mail.password_present ? "（留空保留）" : ""}`} hint="保存后不再显示。">
            <input id="mail-password" className="text-input" name="password" type="password" autoComplete="new-password"
              value={password} onChange={event => setPassword(event.target.value)} />
          </Field>
          <Field className="platform-setting" label="发信地址" hint="要用邮件服务里已验证过的地址。">
            <input id="mail-sender" className="text-input" name="sender" type="email" autoComplete="off" spellCheck={false}
              placeholder="no-reply@example.com" value={sender} onChange={event => setSender(event.target.value)} />
          </Field>
        </div>
        <div className="admin-card-foot">
          <Button variant="primary" type="submit" disabled={saving.pending || !dirty || !complete}
            aria-busy={saving.pending}>
            {saving.pending ? <><LightMotion state="working" compact />正在保存…</> : "保存设置"}
          </Button>
          <CommandFeedback error={saving.error} notice={saving.notice} failed="邮件设置没有保存" />
        </div>
      </fieldset>
    </form>
    <form className="platform-settings admin-mail" noValidate onSubmit={test} aria-busy={testing.pending}>
      <fieldset disabled={testing.pending}>
        <legend>发一封测试邮件</legend>
        <p>{dirty ? "先保存设置，再测试：测试用的是已保存的设置。"
          : view.configured ? "发到你自己的邮箱，确认设置能用。" : "先填写并保存上面的设置。"}</p>
        <div className="admin-card-foot admin-mail-test">
          <Field className="platform-setting" label="收件邮箱">
            <input id="mail-to" className="text-input" name="to" type="email" autoComplete="off" spellCheck={false}
              placeholder="name@example.com" value={to} onChange={event => setTo(event.target.value)} />
          </Field>
          <Button variant="secondary" type="submit" disabled={testing.pending || dirty || !view.configured || !to.trim()}
            aria-busy={testing.pending}>
            {testing.pending ? <><LightMotion state="working" compact />正在发送…</> : "发送测试邮件"}
          </Button>
          <CommandFeedback error={testing.error} notice={testing.notice} failed="测试邮件没有发出" />
        </div>
      </fieldset>
    </form>
  </>;
}
